import * as DialogPrimitive from "@radix-ui/react-dialog";
import { styles } from "@repo/helpers";
import { Maximize2, Minimize2, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { VisuallyHidden } from "radix-ui";
import * as React from "react";

import { Button, ButtonColor, ButtonTypes } from "../../button";

interface Size {
	width: number;
	height: number;
}
interface Position {
	x: number;
	y: number;
}
type Edge = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export interface MovableResizableDialogProps {
	open: boolean;
	onOpenChange?: (open: boolean) => void;
	title?: string;
	initialSize?: Size;
	minSize?: Size;
	resizable?: boolean;
	movable?: boolean;
	minWidth?: number;
	closeOnClickOutside?: boolean;
	closeOnEscape?: boolean;
	children?: React.ReactNode;
	className?: string;
	contentClassName?: string;
	footer?: React.ReactNode;
	footerClassName?: string;
}

const DEFAULT_SIZE: Size = { width: 720, height: 420 };
const DEFAULT_MIN_SIZE: Size = { width: 360, height: 220 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function MoveableDialog({
	open,
	onOpenChange,
	title = "Window",
	initialSize = DEFAULT_SIZE,
	minSize = DEFAULT_MIN_SIZE,
	resizable = false,
	movable = false,
	closeOnClickOutside = false,
	closeOnEscape = false,
	children,
	className,
	contentClassName,
	footer,
	footerClassName,
}: MovableResizableDialogProps) {
	const overlayRef = React.useRef<HTMLDivElement | null>(null);
	const winRef = React.useRef<HTMLDivElement | null>(null);

	const [rect, setRect] = React.useState<{ pos: Position; size: Size }>(() => {
		const centerX = typeof window !== "undefined" ? (window.innerWidth - initialSize.width) / 2 : 100;
		const centerY = typeof window !== "undefined" ? (window.innerHeight - initialSize.height) / 2 : 100;
		return {
			pos: { x: Math.max(0, centerX), y: Math.max(0, centerY) },
			size: initialSize,
		};
	});
	const [prevRect, setPrevRect] = React.useState<{ pos: Position; size: Size } | null>(null);

	// Tracking the "ideal" size and position as intended by the user,
	// so it can be restored when the window grows back after being shrunk.
	const desiredRectRef = React.useRef(rect);
	const desiredPrevRectRef = React.useRef(prevRect);

	// Update refs whenever the state changes (when user manually interacts)
	const updateRect = (newRect: { pos: Position; size: Size }) => {
		setRect(newRect);
		desiredRectRef.current = newRect;
	};

	const updatePrevRect = (newPrevRect: { pos: Position; size: Size } | null) => {
		setPrevRect(newPrevRect);
		desiredPrevRectRef.current = newPrevRect;
	};
	const [dragging, setDragging] = React.useState(false);
	const [resizing, setResizing] = React.useState<Edge | null>(null);
	const [isMaximized, setIsMaximized] = React.useState(false);

	React.useEffect(() => {
		if (!open) return;
		const onResize = () => {
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const margin = 8;

			if (isMaximized) {
				setRect({
					pos: { x: margin, y: margin },
					size: { width: vw - margin * 2, height: vh - margin * 2 },
				});
			} else {
				const r = desiredRectRef.current;
				const width = clamp(r.size.width, minSize.width, vw);
				const height = clamp(r.size.height, minSize.height, vh);
				const x = clamp(r.pos.x, 0, vw - width);
				const y = clamp(r.pos.y, 0, vh - Math.min(height, vh));
				setRect({ pos: { x, y }, size: { width, height } });
			}

			const pr = desiredPrevRectRef.current;
			if (pr) {
				const width = clamp(pr.size.width, minSize.width, vw);
				const height = clamp(pr.size.height, minSize.height, vh);
				const x = clamp(pr.pos.x, 0, vw - width);
				const y = clamp(pr.pos.y, 0, vh - Math.min(height, vh));
				setPrevRect({ pos: { x, y }, size: { width, height } });
			} else {
				setPrevRect(null);
			}
		};
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, [open, minSize.height, minSize.width, isMaximized]);

	React.useEffect(() => {
		if (open) {
			const vw = window.innerWidth;
			const vh = window.innerHeight;

			const r = desiredRectRef.current;
			const width = clamp(r.size.width, minSize.width, vw);
			const height = clamp(r.size.height, minSize.height, vh);
			const x = clamp(r.pos.x, 0, vw - width);
			const y = clamp(r.pos.y, 0, vh - Math.min(height, vh));
			setRect({ pos: { x, y }, size: { width, height } });

			const pr = desiredPrevRectRef.current;
			if (pr) {
				const width = clamp(pr.size.width, minSize.width, vw);
				const height = clamp(pr.size.height, minSize.height, vh);
				const x = clamp(pr.pos.x, 0, vw - width);
				const y = clamp(pr.pos.y, 0, vh - Math.min(height, vh));
				setPrevRect({ pos: { x, y }, size: { width, height } });
			} else {
				setPrevRect(null);
			}

			setTimeout(() => {
				winRef.current?.focus();
			}, 0);
		}
	}, [open, minSize.width, minSize.height]);

	const onTitlePointerDown = (e: React.PointerEvent) => {
		// Dedicated drag handle only; no buttons live inside here
		if (e.button !== 0) return;
		e.preventDefault();
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

		// If maximized, restore and compute pointer offset for natural feel
		let baseRect = rect;
		let pointerOffset: { px: number; py: number } | null = null;
		if (isMaximized) {
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const margin = 8;
			const restored = prevRect ?? {
				pos: { x: margin, y: margin },
				size: { width: vw - margin * 2, height: vh - margin * 2 },
			};
			const ratioX = (e.clientX - vw / 2) / (vw / 2);
			const newX = clamp(
				restored.pos.x + (restored.size.width / 2) * (1 + ratioX) - 40,
				0,
				vw - restored.size.width,
			);
			baseRect = { pos: { x: newX, y: restored.pos.y }, size: restored.size };
			updateRect(baseRect);
			setIsMaximized(false);
			updatePrevRect(null);
			pointerOffset = { px: e.clientX - baseRect.pos.x, py: e.clientY - baseRect.pos.y };
		}

		const startX = e.clientX;
		const startY = e.clientY;
		const startPos = baseRect.pos;

		setDragging(true);
		document.body.style.userSelect = "none";
		document.body.style.cursor = "grabbing";

		const onMove = (ev: PointerEvent) => {
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const dx = ev.clientX - startX;
			const dy = ev.clientY - startY;
			let newX = startPos.x + dx;
			let newY = startPos.y + dy;

			if (pointerOffset) {
				newX = ev.clientX - pointerOffset.px;
				newY = ev.clientY - pointerOffset.py;
			}

			newX = clamp(newX, 0, vw - baseRect.size.width);
			newY = clamp(newY, 0, vh - Math.min(baseRect.size.height, vh));
			updateRect({ pos: { x: newX, y: newY }, size: baseRect.size });
		};

		const onUp = () => {
			setDragging(false);
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};

		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	};

	const onTitleDoubleClick = () => {
		toggleMaximize();
	};

	const toggleMaximize = () => {
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		const margin = 8;
		if (isMaximized) {
			if (desiredPrevRectRef.current) {
				updateRect(desiredPrevRectRef.current);
			} else {
				updateRect({
					pos: { x: margin, y: margin },
					size: { width: vw - margin * 2, height: vh - margin * 2 },
				});
			}
			setIsMaximized(false);
			updatePrevRect(null);
		} else {
			updatePrevRect(desiredRectRef.current);
			setRect({
				pos: { x: margin, y: margin },
				size: { width: vw - margin * 2, height: vh - margin * 2 },
			});
			setIsMaximized(true);
		}
	};

	const startResize = (edge: Edge) => (e: React.PointerEvent) => {
		if (e.button !== 0) return;
		e.preventDefault();
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

		const start = { x: e.clientX, y: e.clientY, rect };
		setResizing(edge);
		document.body.style.userSelect = "none";
		document.body.style.cursor = cursorForEdge(edge);

		const onMove = (ev: PointerEvent) => {
			const vw = window.innerWidth;
			const vh = window.innerHeight;
			const dx = ev.clientX - start.x;
			const dy = ev.clientY - start.y;

			let { x, y } = start.rect.pos;
			let { width, height } = start.rect.size;

			if (edge.includes("e")) {
				width = clamp(width + dx, minSize.width, vw - x);
			}
			if (edge.includes("w")) {
				const maxLeft = x + width - minSize.width;
				const nextX = clamp(x + dx, 0, maxLeft);
				width = width + (x - nextX);
				x = nextX;
			}
			if (edge.includes("s")) {
				height = clamp(height + dy, minSize.height, vh - y);
			}
			if (edge.includes("n")) {
				const maxTop = y + height - minSize.height;
				const nextY = clamp(y + dy, 0, maxTop);
				height = height + (y - nextY);
				y = nextY;
			}

			updateRect({ pos: { x, y }, size: { width, height } });
		};

		const onUp = () => {
			setResizing(null);
			document.body.style.userSelect = "";
			document.body.style.cursor = "";
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
		};

		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	};

	const close = () => onOpenChange?.(false);

	const cursorForEdge = (edge: Edge) => {
		switch (edge) {
			case "n":
			case "s":
				return "ns-resize";
			case "e":
			case "w":
				return "ew-resize";
			case "ne":
			case "sw":
				return "nesw-resize";
			case "nw":
			case "se":
				return "nwse-resize";
		}
	};

	const style: React.CSSProperties = {
		left: rect.pos.x,
		top: rect.pos.y,
		width: rect.size.width,
		height: rect.size.height,
	};

	return (
		<AnimatePresence>
			{open ? (
				<DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={true}>
					<DialogPrimitive.Portal>
						<DialogPrimitive.Overlay asChild>
							<motion.div
								key="overlay"
								ref={overlayRef}
								className={styles("fixed inset-0 z-40")}
								aria-hidden={false}
								role="presentation"
							>
								{/* Backdrop */}
								<motion.div
									className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
								/>
							</motion.div>
						</DialogPrimitive.Overlay>

						<DialogPrimitive.Content
							asChild
							onPointerDownOutside={(e) => {
								if (!closeOnClickOutside) {
									e.preventDefault();
								}
							}}
							onEscapeKeyDown={(e) => {
								if (!closeOnEscape) {
									e.preventDefault();
								}
							}}
						>
							<motion.div
								key="window"
								ref={winRef}
								role="dialog"
								aria-modal="true"
								aria-label={title}
								tabIndex={-1}
								className={styles(
									"isolate flex flex-col outline-none",
									"rounded-xl border shadow-2xl",
									"bg-white dark:bg-neutral-900",
									dragging || resizing
										? "ring-2 ring-blue-400/60"
										: "ring-1 ring-black/5 dark:ring-white/10",
									isMaximized ? "rounded-none" : "rounded-xl",
									"fixed z-50",
									className,
								)}
								style={style}
								initial={{ scale: 0.98, opacity: 0.9 }}
								animate={{
									scale: 1,
									opacity: 1,
									transition: { type: "spring", stiffness: 300, damping: 30 },
								}}
								exit={{ scale: 0.98, opacity: 0, transition: { duration: 0.12 } }}
							>
								{/* Title bar */}
								<div
									className={styles(
										"flex h-11 items-center justify-between gap-2 px-2",
										"bg-gray-300",
										"rounded-t-xl",
									)}
								>
									{/* Drag handle (no buttons inside; safe to preventDefault) */}
									<div
										className={styles(
											"flex min-w-0 flex-1 items-center gap-2 px-1",
											movable && (dragging ? "cursor-grabbing" : "cursor-grab"),
										)}
										onPointerDown={movable ? onTitlePointerDown : undefined}
										onDoubleClick={movable ? onTitleDoubleClick : undefined}
										aria-label={movable ? "Drag window" : undefined}
									>
										<div className="line-clamp-1 select-none text-base !font-semibold">{title}</div>

										<VisuallyHidden.Root>
											<DialogPrimitive.Title>{title}</DialogPrimitive.Title>
										</VisuallyHidden.Root>
									</div>

									{/* Controls */}
									<div className="flex items-center gap-4 pr-1">
										{resizable && (
											<Button
												types={[ButtonTypes.Text]}
												color={ButtonColor.DarkBlue}
												onClick={(e) => {
													e.stopPropagation();
													toggleMaximize();
												}}
												aria-label={isMaximized ? "Restore" : "Maximize"}
												className="flex h-6 w-6 items-center justify-center"
											>
												{isMaximized ? (
													<Minimize2 className="h-4 w-4" />
												) : (
													<Maximize2 className="h-4 w-4" />
												)}
											</Button>
										)}
										<Button
											types={[ButtonTypes.Text]}
											color={ButtonColor.DarkBlue}
											onClick={(e) => {
												e.stopPropagation();
												close();
											}}
											aria-label="Close"
											className="flex h-6 w-6 items-center justify-center"
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								</div>
								{/* Content */}
								<div
									className={styles(
										"relative flex min-h-0 flex-1 flex-col px-4 pb-3 pt-3",
										contentClassName,
									)}
								>
									<div className={styles("flex min-h-0 flex-1 flex-col overflow-auto px-1")}>
										{children}
									</div>
								</div>
								{/* Footer (always visible) */}
								{footer ? (
									<div
										className={styles(
											"border-t border-black/10 bg-white/80 backdrop-blur-sm dark:border-white/10 dark:bg-neutral-900/80",
											"px-4 py-3",
											footerClassName,
										)}
									>
										{footer}
									</div>
								) : null}
								{/* Resize handles */}
								{resizable && !isMaximized && (
									<>
										{/* Corners */}
										<div
											className="absolute -right-1 -top-1 h-4 w-4 cursor-nesw-resize"
											onPointerDown={startResize("ne")}
										/>
										<div
											className="absolute -left-1 -top-1 h-4 w-4 cursor-nwse-resize"
											onPointerDown={startResize("nw")}
										/>
										<div
											className="absolute -bottom-1 -right-1 h-4 w-4 cursor-nwse-resize"
											onPointerDown={startResize("se")}
										/>
										<div
											className="absolute -bottom-1 -left-1 h-4 w-4 cursor-nesw-resize"
											onPointerDown={startResize("sw")}
										/>
										{/* Edges */}
										<div
											className="absolute -top-1 left-2 right-2 h-2 cursor-ns-resize"
											onPointerDown={startResize("n")}
										/>
										<div
											className="absolute -bottom-1 left-2 right-2 h-2 cursor-ns-resize"
											onPointerDown={startResize("s")}
										/>
										<div
											className="absolute -left-1 bottom-2 top-2 w-2 cursor-ew-resize"
											onPointerDown={startResize("w")}
										/>
										<div
											className="absolute -right-1 bottom-2 top-2 w-2 cursor-ew-resize"
											onPointerDown={startResize("e")}
										/>
									</>
								)}
							</motion.div>
						</DialogPrimitive.Content>
					</DialogPrimitive.Portal>
				</DialogPrimitive.Root>
			) : null}
		</AnimatePresence>
	);
}
